import React, {useMemo} from 'react';
import {View} from 'react-native';
import {Text} from '../../../../../../../shared/components/text';
import {useStyles} from '../../../../../../../shared/hooks/styles';
import {datePipe, hexToRgbA} from '../../../../../../../shared/pipes';
import {Props} from './model';
import {BaseField} from '../../../../../../../shared/components/baseField';
import {useRegCardState} from '../../../../esiaRegCardState/useRegCardState';
import {TouchableOpacity} from 'react-native-gesture-handler';
import {capitalizeAndAddDot} from '../../../../../../../shared/utils/text';
import {ParentSaturated} from '../../../../esiaRegCardState/model';

export default ({kidId, userType}: Props) => {
  const {kids, parent, setKids, setParent} = useRegCardState();
  const isCardValid = useMemo(
    () => (userType === 'kid' && kidId != null && kids[kidId]?.errors?.length === 0) || userType === 'parent',
    [kidId, kids],
  );

  const styles = useStyles(({theme, addShadow}) => ({
    container: {
      backgroundColor: theme.color.primaryNonGreyBackground,
      borderRadius: 14,
      width: '100%',
      marginTop: 15,
      ...addShadow({
        height: isCardValid ? 2 : 0,
        radius: isCardValid ? 14 : 0,
        opacity: isCardValid ? 0.25 : 0,
      }),
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      minHeight: 45,
      paddingHorizontal: 20,
      paddingTop: 10,
      position: 'relative',
      borderTopLeftRadius: 14,
      borderTopRightRadius: 14,
      backgroundColor: isCardValid ? 'transperent' : hexToRgbA(theme.applications.mobileListItemBodyBg, 0.55),
    },
    body: {
      borderBottomLeftRadius: 14,
      borderBottomRightRadius: 14,
      backgroundColor: isCardValid ? hexToRgbA(theme.applications.mobileListItemBodyBg, 0.15) : '#DDDDDD',
      paddingHorizontal: 20,
      paddingVertical: 10,
    },
    textNormal: {
      ...theme.fonts.body,
    },
    textInvalidToApply: {
      ...theme.fonts.body,
      color: 'grey',
    },
    errorInfo: {
      ...theme.fonts.bodySmallLight,
      color: '#F15A59',
    },
    checkbox: {
      height: 42,
      width: 56,
      backgroundColor: 'transperent',
      borderRadius: 15,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 10,
    },
  }));

  const handleParentCheckboxClick = (value: boolean) => {
    if (parent != null)
      setParent((prevParent) => {
        if (!prevParent) return prevParent;
        return {
          ...prevParent,
          checked: value,
        } as ParentSaturated;
      });
  };

  const handleKidCheckboxClick = (checkedKidId: number) => {
    kids[checkedKidId].checked = !kids[checkedKidId].checked;
    setKids([...kids]);
  };

  const parentFIO = `${parent?.firstName} ${parent?.middleName ? parent.middleName : ''} ${capitalizeAndAddDot(
    parent?.lastName,
  )}`;
  const childFIO =
    kidId != null && kids[kidId]
      ? `${kids[kidId]?.firstName} ${kids[kidId]?.middleName ? kids[kidId]?.middleName : ''} ${capitalizeAndAddDot(
          kids[kidId]?.lastName,
        )}`
      : '';
  const invalidKidMessageText = () => {
    if (kidId != null && kids[kidId]?.errors?.length) {
      return kids[kidId].errors.join(', ');
    }
  };

  const render = {
    parent: (
      <>
        {parent ? (
          <View style={styles.container}>
            <TouchableOpacity onPress={() => handleParentCheckboxClick(!parent.checked)}>
              <View style={styles.header}>
                <Text style={styles.textNormal}>{parentFIO}</Text>
                <Text style={styles.textNormal}>Дата рождения: {`${datePipe(parent?.birthDate)}`}</Text>
              </View>
              <View style={styles.body}>
                <View style={{marginLeft: 'auto', display: 'flex', flexDirection: 'row'}}>
                  <Text style={{marginTop: 10}}>Подать заявление на выдачу карты:</Text>
                  <View>
                    <View style={styles.checkbox}>
                      <BaseField
                        autoCapitalize='none'
                        input={{
                          value: parent.checked,
                          onChange: (newValue) => handleParentCheckboxClick(newValue),
                        }}
                        type='checkbox'
                        onCustomBlur={() => {}}
                      />
                    </View>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        ) : null}
      </>
    ),
    kid: (
      <>
        {kids && kidId != null ? (
          <View style={styles.container}>
            {isCardValid ? (
              <TouchableOpacity onPress={() => handleKidCheckboxClick(kidId)}>
                <View style={styles.header}>
                  <Text style={styles.textNormal}>{childFIO}</Text>
                  <Text style={styles.textNormal}>Дата рождения: {`${datePipe(kids[kidId]?.birthDate)}`}</Text>
                </View>
                <View style={styles.body} testID='bodyTest'>
                  <View style={{display: 'flex', flexDirection: 'row', marginLeft: 'auto'}}>
                    <Text style={[styles.textNormal, {marginTop: 10}]}>Подать заявление на выдачу карты:</Text>
                    <View style={styles.checkbox}>
                      <BaseField
                        autoCapitalize='none'
                        input={{
                          value: kids[kidId]?.checked,
                        }}
                        type='checkbox'
                        onCustomBlur={() => {}}
                        disabled={!isCardValid}
                      />
                    </View>
                  </View>
                </View>
              </TouchableOpacity>
            ) : (
              <>
                <View style={styles.header}>
                  <Text style={styles.textInvalidToApply}>{childFIO}</Text>
                  <Text style={styles.textInvalidToApply}>Дата рождения: {`${datePipe(kids[kidId]?.birthDate)}`}</Text>
                </View>
                <View style={styles.body} testID='bodyTest'>
                  <View style={{display: 'flex', flexDirection: 'row', marginLeft: 'auto'}}>
                    <Text style={[styles.textInvalidToApply, {marginTop: 10}]}>Подать заявление на выдачу карты:</Text>
                    <View style={styles.checkbox}>
                      <BaseField
                        autoCapitalize='none'
                        input={{
                          value: kids[kidId]?.checked,
                        }}
                        type='checkbox'
                        onCustomBlur={() => {}}
                        disabled={true}
                      />
                    </View>
                  </View>
                  <View>
                    <Text style={[styles.errorInfo, {marginTop: 10}]}>Отсутствуют: {invalidKidMessageText()}</Text>
                  </View>
                </View>
              </>
            )}
          </View>
        ) : null}
      </>
    ),
  };
  return render[userType];
};
